import { InputNumber } from "antd"
import { MinusIcon } from "components/ui/icons/minus/MinusIcon"
import { PlusIcon } from "components/ui/icons/plus/PlusIcon"
import { TextArea } from "components/ui/inputs/TextArea"
import { CUSTOMIZATION_NOTE_MAX_LENGTH } from "./useCustomizationFields.hook"
import style from "./CustomizationSection.module.scss"

const { section, header, title, toggleButton, fields, field, labelRow, label, textareaWrapper, numberInput } = style

// The amount is signed USD with at most two decimals (the endpoint 400s on more), so the input
// carries no minimum — a negative adjustment lowers the object's price and is the common case.
const PRICE_ADJUSTMENT_PRECISION = 2

type Props = {
	isExpanded: boolean
	note: string
	priceAdjustment: number | null
	/**
	 * The `+`: reveal the two fields. It is also the CREATE affordance — in the edit sidebar the
	 * owner stores the customization straight away, empty, so the object has one from the click.
	 */
	onExpand: () => void
	/**
	 * The `−`. It is the REMOVE affordance, not merely a collapse: on a saved customization the
	 * owner deletes it, on one that was never saved it just folds the section away.
	 */
	onRemove: () => void
	onNoteChange: (note: string) => void
	onPriceAdjustmentChange: (value: number | null) => void
	/**
	 * Blur commit. The edit sidebar persists here; the create panels leave it out, since their
	 * form is read once, at Create.
	 */
	onCommit?: () => void
	/**
	 * REQ-431 batch panel only: shown in the note/price fields when the selected objects disagree
	 * (some have none, or hold different values) — `Mixed`, matching every other field in that
	 * panel. Single-object callers never pass these, so their placeholders are unaffected.
	 */
	notePlaceholder?: string
	priceAdjustmentPlaceholder?: string
}

/**
 * The Customization block (REQ-413/414), shared verbatim by the furniture edit sidebar and by the
 * cabinet / Other / countertop create panels — it looks and behaves identically in all four, which
 * is exactly why it is one component rather than four.
 *
 * Two states, and no third: collapsed is the bold header plus a `+`, expanded adds the note and
 * the price adjustment. Neither field is required — the customization exists from the moment the
 * section is opened, and the two values are filled in whenever the user knows them. An object can
 * hold at most ONE (REQ-414), so once the section is expanded there is no "add" affordance left
 * anywhere; the same two fields are what an existing customization is edited through.
 *
 * `notePlaceholder`/`priceAdjustmentPlaceholder` exist only for the REQ-431 batch panel, whose
 * selection can disagree — the single-object callers never pass them.
 */
export const CustomizationSection = ({
	isExpanded,
	note,
	priceAdjustment,
	onExpand,
	onRemove,
	onNoteChange,
	onPriceAdjustmentChange,
	onCommit,
	notePlaceholder,
	priceAdjustmentPlaceholder,
}: Props) => {
	return (
		<div className={section}>
			<div className={header}>
				<span className={title}>Customization</span>
				<button
					type="button"
					className={toggleButton}
					aria-label={isExpanded ? "Remove customization" : "Add customization"}
					// Keeps the focused field from blurring before this click lands. Without it,
					// backing out of a half-typed customization with `−` fires the blur commit
					// first and then the remove — writing to the server on the way to cancelling,
					// and burning two version bumps to end up where it started.
					onMouseDown={(event) => event.preventDefault()}
					onClick={isExpanded ? onRemove : onExpand}
				>
					{isExpanded ? <MinusIcon size={16} color="currentColor" /> : <PlusIcon size={16} color="currentColor" />}
				</button>
			</div>

			{isExpanded ? (
				<div className={fields}>
					<div className={field}>
						{/* No required mark on either field: opening the section is what asks for the
						    customization, and the endpoint stores it with an empty note, a null amount,
						    or both — the user fills them in when they know the numbers. */}
						<div className={labelRow}>
							<span className={label}>Customization note</span>
						</div>
						<div className={textareaWrapper}>
							<TextArea
								value={note}
								placeholder={notePlaceholder ?? "e.g. Cabinet shortened by 2 inches"}
								minRows={2}
								maxLength={CUSTOMIZATION_NOTE_MAX_LENGTH}
								onChange={(e) => onNoteChange(e.target.value)}
								onBlur={onCommit}
							/>
						</div>
					</div>

					<div className={field}>
						<div className={labelRow}>
							<span className={label}>Price adjustment</span>
						</div>
						<InputNumber
							className={numberInput}
							value={priceAdjustment}
							placeholder={priceAdjustmentPlaceholder}
							onChange={(value) => onPriceAdjustmentChange(typeof value === "number" ? value : null)}
							onBlur={onCommit}
							prefix="$"
							controls={false}
							precision={PRICE_ADJUSTMENT_PRECISION}
						/>
					</div>
				</div>
			) : null}
		</div>
	)
}
